'use client';

import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

/**
 * An email address people can actually use.
 *
 * On a phone, `mailto:` opens the mail app, so a tap goes straight there. On a
 * computer it only works if a desktop mail app is set up, and for most people
 * who live in Gmail or Outlook on the web the click does nothing at all. So on
 * a mouse-driven device the click opens a small menu instead: the mail app,
 * Gmail, Outlook, or copy the address. Every option starts a message with a
 * subject already filled in.
 */

const SUBJECT = 'Hello KIT Agency';

export function EmailLink({
  email,
  className,
  children,
  align = 'right',
  subject = SUBJECT,
}: {
  email: string;
  className?: string;
  children?: ReactNode;
  /** Which edge of the link the menu lines up with. */
  align?: 'left' | 'right';
  subject?: string;
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  const id = useId();
  const s = encodeURIComponent(subject);
  const to = encodeURIComponent(email);
  const mailto = `mailto:${email}?subject=${s}`;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        wrap.current?.querySelector<HTMLElement>('[data-email-trigger]')?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard blocked: the address is on screen anyway */
    }
  };

  const item = 'flex min-h-[2.75rem] w-full items-center gap-3 rounded-xl px-3 text-left text-sm font-medium text-mist-100 hover:bg-veil-2';

  return (
    <span ref={wrap} className="relative inline-flex">
      <a
        href={mailto}
        data-email-trigger
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={id}
        onClick={(e) => {
          // Touch screens: let the phone open its mail app.
          if (window.matchMedia('(pointer: coarse)').matches) return;
          e.preventDefault();
          setOpen((v) => !v);
        }}
        className={className}
      >
        {children ?? email}
      </a>
      {open && (
        <span
          id={id}
          className={`pop-in absolute top-full z-[70] mt-2 w-64 rounded-2xl border border-line-2 bg-ink-900 p-2 text-mist-100 shadow-[0_24px_60px_-20px_rgb(0_0_0/0.45)] ${align === 'right' ? 'right-0' : 'left-0'}`}
        >
          <span className="block px-3 pb-1 pt-2 text-xs text-mist-500">Write to {email}</span>
          <a href={mailto} className={item} onClick={() => setOpen(false)}>
            <Icon d="M4 6h16v12H4z M4 7l8 6 8-6" /> Open my mail app
          </a>
          <a href={`https://mail.google.com/mail/?view=cm&fs=1&to=${to}&su=${s}`} target="_blank" rel="noopener" className={item} onClick={() => setOpen(false)}>
            <Icon d="M4 18V7l8 6 8-6v11 M4 7l8 6 8-6" /> Gmail<span className="sr-only"> (opens in a new tab)</span>
          </a>
          <a href={`https://outlook.office.com/mail/deeplink/compose?to=${to}&subject=${s}`} target="_blank" rel="noopener" className={item} onClick={() => setOpen(false)}>
            <Icon d="M3 6h11v12H3z M14 9h7v8h-7 M6.5 12a2 2 0 1 0 4 0 2 2 0 1 0-4 0" /> Outlook<span className="sr-only"> (opens in a new tab)</span>
          </a>
          <button type="button" onClick={copy} className={item}>
            <Icon d="M9 9h11v11H9z M5 15H4V4h11v1" /> <span aria-live="polite">{copied ? 'Copied' : 'Copy the address'}</span>
          </button>
        </span>
      )}
    </span>
  );
}

function Icon({ d }: { d: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0 text-spark-500">
      <path d={d} />
    </svg>
  );
}
